import React, { useState, useEffect } from 'react';
import i18next from '../../i18n.js';
import { attachmentTypes, businessTripFolderSettiongs, acceptImageFileString, allowedImageFileExtensions } from '../../constants.js';
import { getDodumentFiles, copyTextToClipboard } from '../helpers/HelperFunctions.js';
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { viewFile, viewImage, messageDialog, unknownErrorMessageDialog } from '../ModalDialog.js';
import { Icon } from './TripIcons.js';

// Документи відрядження: файли зі сховища заявки, згруповані по папках, і додані посилання.
// Використовується і в підсумку «Сформоване відрядження», і у формі завдань відрядження.
const TripDocuments = (props) => {
    const { properties } = props;
    const [storeFolderContent, setStoreFolderContent] = useState(null);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [copiedItemKey, setCopiedItemKey] = useState(null);

    // Файли лежать у сховищі заявки, а не у властивостях документа, і збираються окремими
    // запитами по кожній папці. Тому вантажимо їх один раз при монтуванні: список рендериться
    // лише там, де його справді показують, отже монтування і є моментом, коли він потрібен.
    const documentId = properties['sys:node-uuid'];
    useEffect(() => {
        if (!documentId) return;
        let canceled = false;
        setDocumentsLoading(true);
        fetch(`/api/document/GetStoreFolderContent/${documentId}`, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
            .then(response => response.ok ? response.json() : null)
            .then(data => { if (!canceled) setStoreFolderContent(data); })
            .catch(() => { if (!canceled) setStoreFolderContent(null); })
            .finally(() => { if (!canceled) setDocumentsLoading(false); });
        return () => { canceled = true; };
    }, [documentId]);

    // порожні папки не показуються, тому лишаємо тільки ті, у яких справді є файли
    const folderFiles = (folder) => (folder.folderContent?.children ?? []).filter(child => !child.isFolder);
    const folderLabel = (folder) => businessTripFolderSettiongs.find(item => item.title === folder.title)?.label ?? folder.title;

    // Сховище не повертає mime-тип, тому чи можна показати файл, визначаємо за розширенням
    const canViewFile = (file) => {
        const extension = (file.title || file.fileName || '').split('.').pop().toLowerCase();
        return extension === 'pdf' || allowedImageFileExtensions.includes(extension);
    }

    const getStoreFile = async (nodeRef) => {
        const result = await getDodumentFiles('/api/file/GetSignedFiles', [{ nodeRef: nodeRef, loadAsPdf: true }]);
        return result?.[0]?.file ?? null;
    }

    const downloadFileHandle = async (item) => {
        const file = await getStoreFile(item.nodeRef);
        if (!file) {
            unknownErrorMessageDialog();
            return;
        }
        const anchor = document.createElement('a');
        anchor.href = file.content;
        anchor.download = file.fileName;
        anchor.click();
    }

    const viewFileHandle = async (item) => {
        const file = await getStoreFile(item.nodeRef);
        if (!file) {
            unknownErrorMessageDialog();
            return;
        }
        if (file.mimeType === 'application/pdf') {
            viewFile(file.content, file.fileName, false);
        }
        else if (acceptImageFileString.split(',').includes(file.mimeType)) {
            viewImage(file.content);
        }
        else {
            messageDialog('', i18next.t('document.notViewedMessage'));
        }
    }

    // підказка «скопійовано» показується біля своєї кнопки і сама зникає
    const copyLinkHandle = (link, key) => {
        copyTextToClipboard(link);
        setCopiedItemKey(key);
        setTimeout(() => setCopiedItemKey(current => (current === key ? null : current)), 2000);
    }

    // Посилання додаються на кроці «Деталі відрядження» в те саме поле, що й файли, але в
    // сховищі документів їх немає, тому вони стають окремою папкою в кінці списку. Показуються
    // разом з рештою — увесь блок чекає на завантаження документів.
    const links = (properties['attachments'] ?? []).filter(item => item.type === attachmentTypes.link);
    const documentFolders = [
        ...(storeFolderContent?.children ?? [])
            .filter(child => child.isFolder && folderFiles(child).length > 0)
            .map(folder => ({
                label: folderLabel(folder),
                items: folderFiles(folder).map(file => ({
                    icon: 'doc',
                    name: file.title || file.fileName,
                    nodeRef: file.nodeRef,
                    canView: canViewFile(file)
                }))
            })),
        ...(links.length > 0
            ? [{
                label: i18next.t('document.tripSummaryLinksFolder'),
                items: links.map(link => ({ icon: 'link', name: link.value, link: link.value }))
            }]
            : [])
    ];

    return (<div className="trip-documents">
        <div className="trip-summary-section-header">{i18next.t('document.tripSummaryDocsHeader')}</div>
        {documentsLoading
            ? <div className="trip-summary-files-loader"><Spinner animation="border" variant="primary" /></div>
            : documentFolders.length > 0
                ? <div className="trip-summary-folders">
                    {documentFolders.map((folder, folderIndex) => <div className="trip-summary-folder" key={folderIndex}>
                        <div className="trip-summary-folder-title">{folder.label}</div>
                        <div className="trip-summary-files">
                            {folder.items.map((item, itemIndex) => {
                                const itemKey = `${folderIndex}-${itemIndex}`;
                                return <div className="trip-summary-file" key={itemIndex}>
                                    <Icon name={item.icon} />
                                    <div className="file-info">
                                        <div className="file-name" title={item.name}>{item.name}</div>
                                    </div>
                                    {item.link
                                        ? <OverlayTrigger placement="top" trigger={[]} show={copiedItemKey === itemKey}
                                            overlay={<Tooltip id={`trip-summary-link-copied-${itemKey}`}>{i18next.t('document.attachmentLinkCopied')}</Tooltip>}>
                                            <button type="button" className="trip-summary-file-action" title={i18next.t('document.attachmentCopyLink')}
                                                onClick={() => copyLinkHandle(item.link, itemKey)}>
                                                <Icon name="copy" size={20} />
                                            </button>
                                        </OverlayTrigger>
                                        : <>
                                            {item.canView && <button type="button" className="trip-summary-file-action" title={i18next.t('view')}
                                                onClick={() => viewFileHandle(item)}>
                                                <Icon name="eye" size={20} />
                                            </button>}
                                            <button type="button" className="trip-summary-file-action" title={i18next.t('download')}
                                                onClick={() => downloadFileHandle(item)}>
                                                <Icon name="download" size={20} />
                                            </button>
                                        </>}
                                </div>;
                            })}
                        </div>
                    </div>)}
                </div>
                : <div className="trip-summary-no-files">{i18next.t('document.tripSummaryNoDocs')}</div>}
    </div>);
}

export default TripDocuments;
